/**
 * 채팅 — 학부모가 상담한 학원 목록 (탭하면 1:1 채팅)
 * 카카오톡 채팅 리스트 스타일: 전체/안읽음 필터 · 원형 프로필 · 시간 · 안읽음 배지
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoChatbubbleEllipses } from 'react-icons/io5';
import { useAuth } from '../../context/AuthContext';
import academyService from '../../services/academyService';

const fmtChatTime = (sec) => {
  if (!sec) return '';
  const d = new Date(sec * 1000);
  const now = new Date();
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, now)) {
    const h = d.getHours();
    const ampm = h < 12 ? '오전' : '오후';
    const h12 = h % 12 || 12;
    return `${ampm} ${h12}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (sameDay(d, yest)) return '어제';
  return `${d.getMonth() + 1}월 ${d.getDate()}일`;
};

const AppChatListPage = () => {
  const navigate = useNavigate();
  const { userData, loading: authLoading } = useAuth();

  const uid = userData?.uid;
  const idKey = (userData?.chatAcademyIds || []).join(',');

  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('전체');

  useEffect(() => {
    const ids = idKey ? idKey.split(',') : [];
    if (!uid || ids.length === 0) { setChats([]); setLoading(false); return undefined; }
    let alive = true;
    (async () => {
      const list = [];
      for (const aid of ids) {
        // eslint-disable-next-line no-await-in-loop
        const [academy, room] = await Promise.all([
          academyService.getAcademy(aid),
          academyService.getChatRoom(aid, uid),
        ]);
        if (academy) {
          list.push({
            academy,
            lastMessage: room?.lastMessage || '',
            ts: room?.updatedAt?.seconds || 0,
            unread: room?.parentUnread || 0,
          });
        }
      }
      if (!alive) return;
      list.sort((a, b) => b.ts - a.ts);
      setChats(list);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [uid, idKey]);

  if (authLoading || (uid && loading)) {
    return (
      <Container>
        <SpinnerWrap><Spinner /></SpinnerWrap>
      </Container>
    );
  }
  if (!uid) {
    return (
      <Container>
        <Empty>
          <EmptyText>로그인하면 채팅 내역을 볼 수 있어요.</EmptyText>
          <EmptyBtn type="button" onClick={() => navigate('/app/login')}>로그인</EmptyBtn>
        </Empty>
      </Container>
    );
  }

  const totalUnread = chats.reduce((s, c) => s + c.unread, 0);
  const filtered = filter === '안읽음' ? chats.filter((c) => c.unread > 0) : chats;

  return (
    <Container>
      {chats.length > 0 && (
        <FilterRow>
          <Chip type="button" $on={filter === '전체'} onClick={() => setFilter('전체')}>
            전체
          </Chip>
          <Chip type="button" $on={filter === '안읽음'} onClick={() => setFilter('안읽음')}>
            <IoChatbubbleEllipses size={15} />
            안읽음
            {totalUnread > 0 && (
              <ChipBadge>{totalUnread > 999 ? '999+' : totalUnread}</ChipBadge>
            )}
          </Chip>
        </FilterRow>
      )}

      {chats.length === 0 ? (
        <Empty>
          <EmptyText>아직 채팅한 학원이 없어요.</EmptyText>
          <EmptyBtn type="button" onClick={() => navigate('/app/home')}>학원 찾아보기</EmptyBtn>
        </Empty>
      ) : filtered.length === 0 ? (
        <Empty>
          <EmptyText>안 읽은 채팅이 없어요.</EmptyText>
        </Empty>
      ) : (
        <List>
          {filtered.map(({ academy, lastMessage, ts, unread }) => (
            <Row key={academy.id} type="button" onClick={() => navigate(`/app/chat/${academy.id}`)}>
              <Photo $url={academy.photoUrl}>
                {!academy.photoUrl && academy.name.slice(0, 1)}
              </Photo>
              <Info>
                <Name>{academy.name}</Name>
                <Last>{lastMessage || '대화를 시작해 보세요'}</Last>
              </Info>
              <Meta>
                <Time>{fmtChatTime(ts)}</Time>
                {unread > 0 && <Badge>{unread > 99 ? '99+' : unread}</Badge>}
              </Meta>
            </Row>
          ))}
        </List>
      )}
    </Container>
  );
};

export default AppChatListPage;

const Container = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 4px 16px 24px;
`;

const SpinnerWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 100px 0;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid #E5E7EB;
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 7px;
  padding: 8px 0 10px;
`;

const Chip = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 7px 14px;
  border-radius: 17px;
  font-size: 13px;
  font-weight: 700;
  color: ${({ $on }) => ($on ? '#fff' : '#4B5563')};
  background: ${({ $on }) => ($on ? '#1A1A2E' : '#fff')};
  border: 1px solid ${({ $on }) => ($on ? '#1A1A2E' : '#E5E7EB')};
  svg { color: ${({ $on, theme }) => ($on ? '#fff' : theme.colors.primary)}; }
`;

const ChipBadge = styled.span`
  min-width: 17px;
  height: 17px;
  padding: 0 5px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10.5px;
  font-weight: 800;
  color: #fff;
  background: #EF4444;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 2px;
  background: none;
  text-align: left;
`;

const Photo = styled.div`
  width: 54px;
  height: 54px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ $url, theme }) =>
    $url ? `#EDEFF3 url(${$url}) center/cover` : theme.colors.primaryLight};
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.p`
  font-size: 15.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Last = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  align-self: flex-start;
  gap: 5px;
  padding-top: 2px;
`;

const Time = styled.span`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const Badge = styled.span`
  min-width: 19px;
  height: 19px;
  padding: 0 5px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  color: #fff;
  background: #EF4444;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 100px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const EmptyBtn = styled.button`
  padding: 11px 24px;
  border-radius: 22px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 13.5px;
  font-weight: 700;
`;
